/**
 * Guards React's DOM mutations against third parties that rewrite the page —
 * chiefly Google Translate, which wraps text nodes in <font> tags and detaches
 * the originals. React then crashes with NotFoundError on removeChild /
 * insertBefore because the nodes it tracks are no longer where it left them
 * (Sentry: NotFoundError on /onboarding, breadcrumbs clicking "font > font").
 *
 * The workaround is the long-standing one from facebook/react#11538: when the
 * child React wants to move/remove isn't actually ours anymore, degrade
 * gracefully instead of throwing.
 */
export function installDomSafetyPatches(): void {
  if (typeof window === 'undefined' || typeof Node === 'undefined') return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      console.warn('domSafety: ignoring removeChild of a detached node (page translation?)');
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      console.warn('domSafety: insertBefore reference detached, appending instead (page translation?)');
      return this.appendChild(newNode) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
